import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, Tooltip as RTooltip, Cell,
} from "recharts";
import {
  Zap, LogOut, Plus, Pencil, Trash2, Copy, ExternalLink, X, Activity,
  Globe, ShieldCheck, Radio, Mail, Send, Filter, Search, RefreshCw,
  Smartphone, Monitor, Tablet, Circle,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative rounded-xl border border-border bg-card/80 backdrop-blur p-5 overflow-hidden hover:border-primary/40 transition-colors"
    >
      <div className={`absolute top-0 left-0 h-0.5 w-full ${color.bar}`} />
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono-head uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon size={18} className={color.text} />
      </div>
      <div className="mt-3 text-3xl font-mono-head font-extrabold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function RouteModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({
    label: "", slug: "", target_url: "", status_code: 307, enabled: true, is_primary: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...editing });
    else setForm({ label: "", slug: "", target_url: "", status_code: 307, enabled: true, is_primary: false });
  }, [editing, open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/routes/${editing.id}`, form);
      else await api.post("/routes", form);
      toast.success(editing ? "Route updated" : "Route created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 relative">
        <button onClick={onClose} data-testid="route-modal-close" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X size={18} />
        </button>
        <h3 className="font-mono-head text-lg font-bold mb-5">
          {editing ? "Edit Route" : "New Redirect Route"}
        </h3>
        <div className="space-y-4">
          <Field label="Label" testid="route-label-input" value={form.label}
            onChange={(v) => setForm({ ...form, label: v })} placeholder="Zero-Stress Method" />
          <Field label="Source Slug (/go/…)" testid="route-slug-input" value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })} placeholder="zero-stress-method" mono />
          <Field label="Target URL" testid="route-target-input" value={form.target_url}
            onChange={(v) => setForm({ ...form, target_url: v })} placeholder="https://book.joe-fenix-method.com/zero-stress-method" mono />
          <div>
            <label className="text-xs font-mono-head uppercase tracking-widest text-muted-foreground">Status Code</label>
            <div className="mt-1.5 flex gap-2">
              {[301, 307].map((c) => (
                <button
                  key={c}
                  data-testid={`route-status-${c}`}
                  onClick={() => setForm({ ...form, status_code: c })}
                  className={`flex-1 py-2 rounded-md font-mono-head text-sm border transition-colors ${
                    form.status_code === c
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {c} · {c === 301 ? "Permanent" : "Temporary"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Toggle label="Enabled" testid="route-enabled-toggle" checked={form.enabled}
              onChange={(v) => setForm({ ...form, enabled: v })} />
            <Toggle label="Primary /go" testid="route-primary-toggle" checked={form.is_primary}
              onChange={(v) => setForm({ ...form, is_primary: v })} />
          </div>
        </div>
        <button
          data-testid="route-save-btn"
          onClick={save}
          disabled={saving || !form.label || !form.slug || !form.target_url}
          className="mt-6 w-full bg-primary text-primary-foreground font-mono-head font-semibold py-2.5 rounded-md hover:bg-[#E04800] transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : editing ? "Update Route" : "Deploy Route"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, mono }) {
  return (
    <div>
      <label className="text-xs font-mono-head uppercase tracking-widest text-muted-foreground">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1.5 w-full bg-background border border-input rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors ${mono ? "font-code" : ""}`}
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2"
      type="button"
    >
      <span className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-primary" : "bg-secondary"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      <span className="text-sm font-mono-head">{label}</span>
    </button>
  );
}

const DeviceIcon = ({ d }) =>
  d === "Mobile" ? <Smartphone size={14} /> : d === "Tablet" ? <Tablet size={14} /> : <Monitor size={14} />;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("routes");
  const [stats, setStats] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filters, setFilters] = useState({ status_code: "", webview: "", device: "", search: "" });
  const [sending, setSending] = useState(false);

  const loadCore = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/stats"), api.get("/routes")]);
      setStats(s.data);
      setRoutes(r.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  const loadLogs = useCallback(async () => {
    const params = {};
    if (filters.status_code) params.status_code = filters.status_code;
    if (filters.webview) params.webview = filters.webview;
    if (filters.device) params.device = filters.device;
    if (filters.search) params.search = filters.search;
    const { data } = await api.get("/logs", { params });
    setLogs(data);
  }, [filters]);

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  }, []);

  useEffect(() => { loadCore(); loadSettings(); }, [loadCore, loadSettings]);
  useEffect(() => { if (tab === "logs") loadLogs(); }, [tab, loadLogs]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const copyShort = (slug) => {
    navigator.clipboard.writeText(`${BACKEND_URL}/go/${slug}`);
    toast.success("Short URL copied");
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/routes/${deleting.id}`);
      toast.success("Route deleted");
      setDeleting(null);
      loadCore();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.put("/settings", patch);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const sendSummary = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/settings/send-summary");
      toast.success(`Summary sent to ${data.recipient} (${data.count} redirects)`);
      loadSettings();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Terminal banner */}
      <div className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-8 w-8 rounded bg-primary text-primary-foreground glow-primary">
              <Zap size={18} />
            </span>
            <div className="font-code text-xs hidden sm:block">
              <span className="text-muted-foreground">HOST </span>
              <span className="text-accent">book.joe-fenix-method.com</span>
              <span className="text-muted-foreground"> · ENGINE </span>
              <span className="text-emerald-400 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 ping-dot inline-block" /> ONLINE
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono-head text-muted-foreground hidden md:inline">{user?.email}</span>
            <button
              data-testid="refresh-btn"
              onClick={() => { loadCore(); if (tab === "logs") loadLogs(); }}
              className="p-2 rounded-md border border-border hover:border-accent hover:text-accent transition-colors"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="inline-flex items-center gap-1.5 text-xs font-mono-head px-3 py-2 rounded-md border border-border hover:border-destructive hover:text-destructive transition-colors"
            >
              <LogOut size={14} /> LOGOUT
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="font-mono-head text-2xl sm:text-3xl font-extrabold tracking-tight">
          Network Command Center
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Zero-Click deep-link routing · 301 / 307 redirection · webview breakout
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatCard testid="stat-total" icon={Activity} label="Total Redirects"
            value={stats?.total_redirects ?? "—"} sub={`${stats?.last_24h ?? 0} in last 24h`}
            color={{ bar: "bg-primary", text: "text-primary" }} />
          <StatCard testid="stat-webview" icon={Smartphone} label="Webview Hits"
            value={stats?.webview_hits ?? "—"} sub={`${stats?.webview_pct ?? 0}% of traffic`}
            color={{ bar: "bg-[#EC4899]", text: "text-[#EC4899]" }} />
          <StatCard testid="stat-routes" icon={Globe} label="Active Routes"
            value={stats?.routes_count ?? "—"} sub={`${stats?.status_301 ?? 0}×301 · ${stats?.status_307 ?? 0}×307`}
            color={{ bar: "bg-accent", text: "text-accent" }} />
          <StatCard testid="stat-tracking" icon={ShieldCheck} label="3rd-Party Tracking"
            value="OFF" sub="internal logs only"
            color={{ bar: "bg-emerald-500", text: "text-emerald-400" }} />
        </div>

        {/* Timeline chart */}
        <div className="mt-4 rounded-xl border border-border bg-card/80 backdrop-blur p-5">
          <div className="flex items-center gap-2 text-xs font-mono-head uppercase tracking-widest text-muted-foreground mb-3">
            <Radio size={14} className="text-accent" /> 7-Day Redirect Volume
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={stats?.timeline ?? []}>
              <XAxis dataKey="date" tick={{ fill: "#8b93a7", fontSize: 11, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <RTooltip
                cursor={{ fill: "rgba(255,85,0,0.08)" }}
                contentStyle={{ background: "#12141C", border: "1px solid #232738", borderRadius: 8, fontFamily: "monospace", fontSize: 12 }}
                labelStyle={{ color: "#fff" }}
              />
              <Bar dataKey="hits" radius={[4, 4, 0, 0]}>
                {(stats?.timeline ?? []).map((_, i) => <Cell key={i} fill="#FF5500" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-border">
          {[
            { id: "routes", label: "Routes", icon: Globe },
            { id: "logs", label: "Analytics Logs", icon: Activity },
            { id: "email", label: "Email Digest", icon: Mail },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 font-mono-head text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {/* ROUTES TAB */}
        {tab === "routes" && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground font-code">{routes.length} route(s) configured</div>
              <button
                data-testid="new-route-btn"
                onClick={() => { setEditing(null); setModalOpen(true); }}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-head text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#E04800] transition-colors glow-primary"
              >
                <Plus size={16} /> New Route
              </button>
            </div>
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="routes-table">
                  <thead className="bg-secondary/50 font-mono-head text-[11px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3">Route</th>
                      <th className="text-left px-4 py-3">Target</th>
                      <th className="text-left px-4 py-3">Code</th>
                      <th className="text-left px-4 py-3">Hits</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map((r) => (
                      <tr key={r.id} data-testid={`route-row-${r.slug}`} className="border-t border-border hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium flex items-center gap-2">
                            {r.label}
                            {r.is_primary && <span className="text-[10px] font-mono-head bg-primary/15 text-primary px-1.5 py-0.5 rounded">PRIMARY</span>}
                          </div>
                          <div className="font-code text-xs text-accent">/go/{r.slug}</div>
                        </td>
                        <td className="px-4 py-3 font-code text-xs text-muted-foreground max-w-[220px] truncate">{r.target_url}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] font-mono-head px-2 py-0.5 rounded ${r.status_code === 301 ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                            {r.status_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono-head">{r.hits ?? 0}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-mono-head ${r.enabled ? "text-emerald-400" : "text-muted-foreground"}`}>
                            <Circle size={8} fill="currentColor" /> {r.enabled ? "LIVE" : "OFF"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button data-testid={`copy-${r.slug}`} onClick={() => copyShort(r.slug)} title="Copy short URL" className="p-1.5 rounded hover:bg-secondary hover:text-accent transition-colors"><Copy size={15} /></button>
                            <a data-testid={`open-${r.slug}`} href={`/go/${r.slug}`} target="_blank" rel="noreferrer" title="Test link" className="p-1.5 rounded hover:bg-secondary hover:text-accent transition-colors"><ExternalLink size={15} /></a>
                            <button data-testid={`edit-${r.slug}`} onClick={() => { setEditing(r); setModalOpen(true); }} title="Edit" className="p-1.5 rounded hover:bg-secondary hover:text-primary transition-colors"><Pencil size={15} /></button>
                            <button data-testid={`delete-${r.slug}`} onClick={() => setDeleting(r)} title="Delete" className="p-1.5 rounded hover:bg-secondary hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {routes.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No routes yet. Deploy your first redirect.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* LOGS TAB */}
        {tab === "logs" && (
          <div className="mt-5">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="logs-search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search slug / referrer / app…"
                  className="w-full bg-card border border-border rounded-md pl-9 pr-3 py-2 text-sm font-code focus:outline-none focus:border-primary"
                />
              </div>
              <select data-testid="filter-status" value={filters.status_code} onChange={(e) => setFilters({ ...filters, status_code: e.target.value })}
                className="bg-card border border-border rounded-md px-3 py-2 text-sm font-mono-head focus:outline-none focus:border-primary">
                <option value="">All Codes</option>
                <option value="301">301</option>
                <option value="307">307</option>
              </select>
              <select data-testid="filter-webview" value={filters.webview} onChange={(e) => setFilters({ ...filters, webview: e.target.value })}
                className="bg-card border border-border rounded-md px-3 py-2 text-sm font-mono-head focus:outline-none focus:border-primary">
                <option value="">All Clients</option>
                <option value="true">Webview</option>
                <option value="false">Native</option>
              </select>
              <select data-testid="filter-device" value={filters.device} onChange={(e) => setFilters({ ...filters, device: e.target.value })}
                className="bg-card border border-border rounded-md px-3 py-2 text-sm font-mono-head focus:outline-none focus:border-primary">
                <option value="">All Devices</option>
                <option value="Mobile">Mobile</option>
                <option value="Tablet">Tablet</option>
                <option value="Desktop">Desktop</option>
              </select>
              <button data-testid="apply-filters-btn" onClick={loadLogs} className="inline-flex items-center gap-2 border border-border px-3 py-2 rounded-md text-sm font-mono-head hover:border-primary hover:text-primary transition-colors">
                <Filter size={15} /> Apply
              </button>
            </div>

            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto max-h-[520px]">
                <table className="w-full text-sm" data-testid="analytics-table">
                  <thead className="bg-secondary/50 font-mono-head text-[11px] uppercase tracking-widest text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3">Time (UTC)</th>
                      <th className="text-left px-4 py-3">Route</th>
                      <th className="text-left px-4 py-3">Code</th>
                      <th className="text-left px-4 py-3">Client</th>
                      <th className="text-left px-4 py-3">Browser / OS</th>
                      <th className="text-left px-4 py-3">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id} className="border-t border-border hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-2.5 font-code text-xs text-muted-foreground whitespace-nowrap">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                        <td className="px-4 py-2.5 font-code text-xs text-accent">/{l.slug}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[11px] font-mono-head px-1.5 py-0.5 rounded ${l.status_code === 301 ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{l.status_code}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          {l.is_webview ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-mono-head text-[#EC4899]"><Smartphone size={12} /> {l.webview_app || "In-App"}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-mono-head text-muted-foreground"><DeviceIcon d={l.device} /> {l.device}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs">{l.browser} <span className="text-muted-foreground">/ {l.os}</span></td>
                        <td className="px-4 py-2.5 font-code text-xs text-muted-foreground max-w-[180px] truncate">{l.referrer}</td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No redirect events match these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* EMAIL TAB */}
        {tab === "email" && settings && (
          <div className="mt-5 grid lg:grid-cols-2 gap-5">
            <div className="rounded-xl border border-border bg-card/80 backdrop-blur p-6">
              <div className="flex items-center gap-2 font-mono-head font-bold mb-1"><Mail size={18} className="text-primary" /> Daily Log Digest</div>
              <p className="text-sm text-muted-foreground mb-5">Route redirect logs directly to an email inbox. No external analytics providers involved.</p>

              <label className="text-xs font-mono-head uppercase tracking-widest text-muted-foreground">Recipient Email</label>
              <input
                data-testid="settings-recipient-input"
                value={settings.recipient_email || ""}
                onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
                onBlur={(e) => saveSettings({ recipient_email: e.target.value })}
                className="mt-1.5 mb-4 w-full bg-background border border-input rounded-md px-3 py-2.5 text-sm font-code focus:outline-none focus:border-primary"
              />

              <div className="flex items-center justify-between py-3 border-t border-border">
                <div>
                  <div className="text-sm font-medium">Daily Summary</div>
                  <div className="text-xs text-muted-foreground">One digest per day</div>
                </div>
                <Toggle testid="settings-digest-toggle" label="" checked={settings.daily_digest_enabled}
                  onChange={(v) => saveSettings({ daily_digest_enabled: v })} />
              </div>

              <div className="flex items-center justify-between py-3 border-t border-border">
                <div>
                  <div className="text-sm font-medium">Send Hour (UTC)</div>
                  <div className="text-xs text-muted-foreground">When the daily digest is dispatched</div>
                </div>
                <select
                  data-testid="settings-hour-select"
                  value={settings.send_hour_utc}
                  onChange={(e) => saveSettings({ send_hour_utc: parseInt(e.target.value, 10) })}
                  className="bg-background border border-input rounded-md px-3 py-2 text-sm font-mono-head focus:outline-none focus:border-primary"
                >
                  {Array.from({ length: 24 }).map((_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                  ))}
                </select>
              </div>

              <button
                data-testid="send-summary-btn"
                onClick={sendSummary}
                disabled={sending}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground font-mono-head font-semibold py-2.5 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send size={16} /> {sending ? "Sending…" : "Send Summary Now"}
              </button>
              {settings.last_sent_date && (
                <div className="mt-3 text-xs text-muted-foreground font-code text-center">Last sent: {settings.last_sent_date}</div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card/80 backdrop-blur p-6">
              <div className="flex items-center gap-2 font-mono-head font-bold mb-4"><ShieldCheck size={18} className="text-emerald-400" /> Security Compliance</div>
              <ul className="space-y-3 text-sm">
                {[
                  "No Google Analytics, Meta Pixel or 3rd-party trackers",
                  "Logs generated internally in your database",
                  "Delivered only to your configured inbox",
                  "Sender: " + (settings.from_name || "Fenix Zero-Click Redirector"),
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Circle size={8} className="mt-1.5 text-emerald-400" fill="currentColor" />
                    <span className="text-muted-foreground">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-4 border-t border-border font-code text-xs text-muted-foreground">
                <div>ROUTE MAP</div>
                <div className="mt-1 text-foreground">
                  <span className="text-accent">{settings.base_host}</span>{settings.base_path}
                  <span className="text-primary"> ⟶ </span>
                  <span className="text-emerald-400">/zero-stress-method</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <RouteModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={loadCore} editing={editing} />

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 font-mono-head font-bold text-destructive"><Trash2 size={18} /> Delete Route</div>
            <p className="text-sm text-muted-foreground mt-3">
              Delete <span className="text-foreground font-code">/go/{deleting.slug}</span>? This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <button data-testid="cancel-delete-btn" onClick={() => setDeleting(null)} className="flex-1 border border-border py-2 rounded-md font-mono-head text-sm hover:bg-secondary transition-colors">Cancel</button>
              <button data-testid="confirm-delete-btn" onClick={confirmDelete} className="flex-1 bg-destructive text-destructive-foreground py-2 rounded-md font-mono-head text-sm hover:opacity-90 transition-opacity">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
